import { supabase, generateSessionCode, getClientId, isValidVote } from './supabase';

// Re-export utility functions
export { getClientId };

/**
 * Parse issue URL to extract platform, number, and other metadata
 * @param {string} url - Issue URL
 * @returns {object|null} Parsed issue info or null if invalid
 */
export const parseIssueUrl = (url) => {
  if (!url || typeof url !== 'string') return null;

  try {
    const urlObj = new URL(url.trim());
    const hostname = urlObj.hostname.toLowerCase();
    const pathname = urlObj.pathname;

    // GitHub pattern: github.com/owner/repo/issues/123
    if (hostname.includes('github.com')) {
      const githubMatch = pathname.match(/^\/([^/]+)\/([^/]+)\/issues\/(\d+)/);
      if (githubMatch) {
        return {
          platform: 'github',
          owner: githubMatch[1],
          repo: githubMatch[2],
          number: githubMatch[3],
          url: url.trim()
        };
      }
    }

    // GitLab pattern: gitlab.com/owner/repo/-/issues/123 or gitlab.com/owner/repo/-/merge_requests/456
    if (hostname.includes('gitlab.com') || hostname.includes('gitlab')) {
      const gitlabIssueMatch = pathname.match(/^\/([^/]+(?:\/[^/]+)*)\/\-\/(issues|merge_requests)\/(\d+)/);
      if (gitlabIssueMatch) {
        return {
          platform: 'gitlab',
          project: gitlabIssueMatch[1],
          type: gitlabIssueMatch[2],
          number: gitlabIssueMatch[3],
          url: url.trim()
        };
      }
    }

    return null;
  } catch (error) {
    console.error('Error parsing issue URL:', error);
    return null;
  }
};

/**
 * Create a new planning poker session
 * @returns {Promise<{success: boolean, code?: string, url?: string, error?: string}>}
 */
export const createSession = async () => {
  try {
    let code = generateSessionCode();
    let attempts = 0;
    const maxAttempts = 10;

    // Ensure unique code
    while (attempts < maxAttempts) {
      const { data: existing } = await supabase
        .from('sessions')
        .select('code')
        .eq('code', code)
        .single();

      if (!existing) break;

      code = generateSessionCode();
      attempts++;
    }

    if (attempts === maxAttempts) {
      return { success: false, error: 'Failed to generate unique session code' };
    }

    const moderatorId = getClientId();

    // Create session
    const { data: session, error } = await supabase
      .from('sessions')
      .insert({
        code,
        moderator_id: moderatorId,
        revealed: false
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating session:', error);
      return { success: false, error: error.message };
    }

    // Add moderator as a player
    await supabase.from('players').insert({
      session_code: code,
      client_id: moderatorId,
      name: 'Moderator',
      connected: true
    });

    const url = `${import.meta.env.VITE_BASE_URL}/player?code=${code}`;

    return { success: true, code, url };
  } catch (error) {
    console.error('Error in createSession:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Join an existing session
 * @param {string} code - Session code
 * @param {string} name - Player name
 * @returns {Promise<{success: boolean, session?: object, error?: string}>}
 */
export const joinSession = async (code, name) => {
  try {
    // Check if session exists
    const { data: session, error: sessionError } = await supabase
      .from('sessions')
      .select('*')
      .eq('code', code.toUpperCase())
      .single();

    if (sessionError || !session) {
      return { success: false, error: 'Session not found' };
    }

    const clientId = getClientId();

    // Check if player already exists in this session
    const { data: existingPlayer } = await supabase
      .from('players')
      .select('*')
      .eq('session_code', code.toUpperCase())
      .eq('client_id', clientId)
      .single();

    if (existingPlayer) {
      // Update existing player
      await supabase
        .from('players')
        .update({
          name,
          connected: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', existingPlayer.id);
    } else {
      // Add new player
      await supabase.from('players').insert({
        session_code: code.toUpperCase(),
        client_id: clientId,
        name,
        connected: true
      });
    }

    // Fetch full session data
    const sessionData = await getSessionData(code.toUpperCase());

    return { success: true, session: sessionData };
  } catch (error) {
    console.error('Error in joinSession:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Get session data with players and rounds
 * @param {string} code - Session code
 * @param {boolean} includeVotes - Whether to include actual votes or hide them
 * @returns {Promise<object|null>}
 */
export const getSessionData = async (code, includeVotes = false) => {
  try {
    // Get session
    const { data: session, error: sessionError } = await supabase
      .from('sessions')
      .select('*')
      .eq('code', code.toUpperCase())
      .single();

    if (sessionError || !session) {
      return null;
    }

    // Get players
    const { data: players, error: playersError } = await supabase
      .from('players')
      .select('*')
      .eq('session_code', code.toUpperCase())
      .eq('connected', true)
      .order('joined_at', { ascending: true });

    if (playersError) {
      console.error('Error fetching players:', playersError);
      return null;
    }

    // Get rounds
    const { data: rounds, error: roundsError } = await supabase
      .from('rounds')
      .select('*')
      .eq('session_code', code.toUpperCase())
      .order('completed_at', { ascending: false });

    if (roundsError) {
      console.error('Error fetching rounds:', roundsError);
    }

    // Format player data (hide votes if not revealed and includeVotes is false)
    const formattedPlayers = players.map((player) => ({
      id: player.client_id,
      name: player.name,
      vote: session.revealed || includeVotes ? player.vote : player.vote ? '✓' : null
    }));

    return {
      code: session.code,
      moderatorId: session.moderator_id,
      topic: session.topic,
      revealed: session.revealed,
      issue: session.issue_url ? {
        url: session.issue_url,
        platform: session.issue_platform,
        title: session.issue_title,
        number: session.issue_number
      } : null,
      players: formattedPlayers,
      rounds: rounds || []
    };
  } catch (error) {
    console.error('Error in getSessionData:', error);
    return null;
  }
};

/**
 * Set the topic for a session
 * @param {string} code - Session code
 * @param {string} topic - Topic text
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const setTopic = async (code, topic) => {
  try {
    const clientId = getClientId();

    // Verify user is moderator
    const { data: session } = await supabase
      .from('sessions')
      .select('moderator_id')
      .eq('code', code.toUpperCase())
      .single();

    if (!session || session.moderator_id !== clientId) {
      return { success: false, error: 'Only moderator can set topic' };
    }

    const { error } = await supabase
      .from('sessions')
      .update({ topic })
      .eq('code', code.toUpperCase());

    if (error) {
      console.error('Error setting topic:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in setTopic:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Set the linked issue for a session
 * @param {string} code - Session code
 * @param {string} issueUrl - Issue URL (GitHub or GitLab)
 * @param {string} issueTitle - Optional issue title
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const setIssue = async (code, issueUrl, issueTitle = null) => {
  try {
    const clientId = getClientId();

    // Verify user is moderator
    const { data: session } = await supabase
      .from('sessions')
      .select('moderator_id')
      .eq('code', code.toUpperCase())
      .single();

    if (!session || session.moderator_id !== clientId) {
      return { success: false, error: 'Only moderator can set issue' };
    }

    // If issueUrl is empty, clear the issue
    if (!issueUrl || issueUrl.trim() === '') {
      const { error } = await supabase
        .from('sessions')
        .update({
          issue_url: null,
          issue_platform: null,
          issue_title: null,
          issue_number: null
        })
        .eq('code', code.toUpperCase());

      if (error) {
        console.error('Error clearing issue:', error);
        return { success: false, error: error.message };
      }

      return { success: true };
    }

    // Parse issue URL
    const issueInfo = parseIssueUrl(issueUrl);

    if (!issueInfo) {
      return { success: false, error: 'Invalid issue URL. Please use a valid GitHub or GitLab issue URL.' };
    }

    // Prepare update data
    const updateData = {
      issue_url: issueInfo.url,
      issue_platform: issueInfo.platform,
      issue_number: issueInfo.platform === 'github'
        ? `#${issueInfo.number}`
        : issueInfo.type === 'merge_requests'
          ? `!${issueInfo.number}`
          : `#${issueInfo.number}`,
      issue_title: issueTitle || null
    };

    const { error } = await supabase
      .from('sessions')
      .update(updateData)
      .eq('code', code.toUpperCase());

    if (error) {
      console.error('Error setting issue:', error);
      return { success: false, error: error.message };
    }

    return { success: true, issueInfo };
  } catch (error) {
    console.error('Error in setIssue:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Submit a vote
 * @param {string} code - Session code
 * @param {string} vote - Vote value
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const submitVote = async (code, vote) => {
  try {
    if (!isValidVote(vote)) {
      return { success: false, error: 'Invalid vote value' };
    }

    const clientId = getClientId();

    const { error } = await supabase
      .from('players')
      .update({ vote })
      .eq('session_code', code.toUpperCase())
      .eq('client_id', clientId);

    if (error) {
      console.error('Error submitting vote:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in submitVote:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Calculate statistics from votes
 * @param {Array} votes - Array of vote values
 * @returns {object} Statistics object
 */
const calculateStatistics = (votes) => {
  const numericVotes = votes
    .filter((v) => v && !isNaN(Number(v)))
    .map(Number)
    .sort((a, b) => a - b);

  if (numericVotes.length === 0) {
    return { average: null, median: null, min: null, max: null };
  }

  const sum = numericVotes.reduce((a, b) => a + b, 0);
  const average = sum / numericVotes.length;
  const median =
    numericVotes.length % 2 === 0
      ? (numericVotes[numericVotes.length / 2 - 1] + numericVotes[numericVotes.length / 2]) / 2
      : numericVotes[Math.floor(numericVotes.length / 2)];
  const min = numericVotes[0];
  const max = numericVotes[numericVotes.length - 1];

  return {
    average: Math.round(average * 10) / 10,
    median,
    min,
    max
  };
};

/**
 * Reveal votes
 * @param {string} code - Session code
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const revealVotes = async (code) => {
  try {
    const clientId = getClientId();

    // Verify user is moderator
    const { data: session } = await supabase
      .from('sessions')
      .select('moderator_id')
      .eq('code', code.toUpperCase())
      .single();

    if (!session || session.moderator_id !== clientId) {
      return { success: false, error: 'Only moderator can reveal votes' };
    }

    const { error } = await supabase
      .from('sessions')
      .update({ revealed: true })
      .eq('code', code.toUpperCase());

    if (error) {
      console.error('Error revealing votes:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in revealVotes:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Reset votes and start a new round
 * @param {string} code - Session code
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const resetVotes = async (code) => {
  try {
    const clientId = getClientId();

    // Verify user is moderator
    const { data: session } = await supabase
      .from('sessions')
      .select('*')
      .eq('code', code.toUpperCase())
      .single();

    if (!session || session.moderator_id !== clientId) {
      return { success: false, error: 'Only moderator can reset votes' };
    }

    // Get current players and votes
    const { data: players } = await supabase
      .from('players')
      .select('*')
      .eq('session_code', code.toUpperCase())
      .eq('connected', true);

    // If votes were revealed, save to rounds history
    if (session.revealed && players.some((p) => p.vote)) {
      const votes = players.map((p) => ({
        name: p.name,
        vote: p.vote
      }));

      const voteValues = players.map((p) => p.vote).filter(Boolean);
      const stats = calculateStatistics(voteValues);

      await supabase.from('rounds').insert({
        session_code: code.toUpperCase(),
        topic: session.topic,
        votes,
        average: stats.average,
        median: stats.median,
        min: stats.min,
        max: stats.max,
        issue_url: session.issue_url,
        issue_platform: session.issue_platform,
        issue_title: session.issue_title,
        issue_number: session.issue_number
      });
    }

    // Reset votes for all players
    await supabase
      .from('players')
      .update({ vote: null })
      .eq('session_code', code.toUpperCase());

    // Reset session (clear topic and issue for new round)
    await supabase
      .from('sessions')
      .update({
        revealed: false,
        topic: null,
        issue_url: null,
        issue_platform: null,
        issue_title: null,
        issue_number: null
      })
      .eq('code', code.toUpperCase());

    return { success: true };
  } catch (error) {
    console.error('Error in resetVotes:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Disconnect a player from a session
 * @param {string} code - Session code
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const disconnectFromSession = async (code) => {
  try {
    const clientId = getClientId();

    const { error } = await supabase
      .from('players')
      .update({ connected: false })
      .eq('session_code', code.toUpperCase())
      .eq('client_id', clientId);

    if (error) {
      console.error('Error disconnecting:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in disconnectFromSession:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Kick a player from the session (moderator only)
 * @param {string} code - Session code
 * @param {string} playerClientId - Client ID of player to kick
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export const kickPlayer = async (code, playerClientId) => {
  try {
    const clientId = getClientId();

    // Verify user is moderator
    const { data: session } = await supabase
      .from('sessions')
      .select('moderator_id')
      .eq('code', code.toUpperCase())
      .single();

    if (!session || session.moderator_id !== clientId) {
      return { success: false, error: 'Only moderator can kick players' };
    }

    // Prevent moderator from kicking themselves
    if (playerClientId === clientId) {
      return { success: false, error: 'Cannot kick yourself' };
    }

    // Set player as disconnected
    const { error } = await supabase
      .from('players')
      .update({ connected: false })
      .eq('session_code', code.toUpperCase())
      .eq('client_id', playerClientId);

    if (error) {
      console.error('Error kicking player:', error);
      return { success: false, error: error.message };
    }

    return { success: true };
  } catch (error) {
    console.error('Error in kickPlayer:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Subscribe to session updates
 * @param {string} code - Session code
 * @param {Function} callback - Callback function to handle updates
 * @returns {object} Supabase channel subscription
 */
export const subscribeToSession = (code, callback) => {
  const channelName = `session:${code.toUpperCase()}`;
  console.log(`[Realtime] Subscribing to channel: ${channelName}`);

  const channel = supabase
    .channel(channelName)
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'sessions',
        filter: `code=eq.${code.toUpperCase()}`
      },
      (payload) => {
        console.log('[Realtime] Session update:', payload);
        callback({ type: 'session', payload });
      }
    )
    .on(
      'postgres_changes',
      {
        event: '*',
        schema: 'public',
        table: 'players',
        filter: `session_code=eq.${code.toUpperCase()}`
      },
      (payload) => {
        console.log('[Realtime] Players update:', payload);
        callback({ type: 'players', payload });
      }
    )
    .on(
      'postgres_changes',
      {
        event: 'INSERT',
        schema: 'public',
        table: 'rounds',
        filter: `session_code=eq.${code.toUpperCase()}`
      },
      (payload) => {
        console.log('[Realtime] Rounds update:', payload);
        callback({ type: 'rounds', payload });
      }
    )
    .subscribe((status, err) => {
      if (status === 'SUBSCRIBED') {
        console.log(`[Realtime] Successfully subscribed to ${channelName}`);
      } else if (status === 'CHANNEL_ERROR') {
        console.error(`[Realtime] Error subscribing to ${channelName}:`, err);
      } else if (status === 'TIMED_OUT') {
        console.error(`[Realtime] Subscription to ${channelName} timed out`);
      } else {
        console.log(`[Realtime] Subscription status: ${status}`);
      }
    });

  return channel;
};

/**
 * Unsubscribe from session updates
 * @param {object} channel - Supabase channel subscription
 */
export const unsubscribeFromSession = async (channel) => {
  if (channel) {
    await supabase.removeChannel(channel);
  }
};
